import BlogCta from 'components/BlogCta';
import DateFormatter from 'components/DateFormatter';
import PostCta from 'components/PostCta';
import ReadCount from 'components/ReadCount';
import ReadTime from 'components/ReadTime';
import RelatedPosts from 'components/RelatedPosts';
import Yoast from 'components/Yoast';
import { Section } from 'elements/Containers';
import { ButtonSmall, InputMedium } from 'elements/Forms';
import { Heading2 } from 'elements/Typography';
import { useAnalytics } from 'hooks/useAnalytics';
import Layout from 'layouts/Default';
import Image from 'next/image';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { useState } from 'react';

import Canonical from 'components/Canonical';
import { getPost } from 'lib/api';
import { contentParser } from 'lib/parser';

const Post = ({ postData, relatedPosts }) => {
  // eslint-disable-next-line prefer-const
  const { content, contents } = contentParser(postData.content);

  const { identify, track } = useAnalytics();
  const [subscribed, setSubscribed] = useState(false);

  const blogSubscribe = async (event) => {
    event.preventDefault();

    let email = event.target.email.value;
    email = email.trim().toLowerCase();

    setSubscribed(true);

    await identify(email);
    await track('Subscribed to the Blog', {
      email,
    });
  };

  return (
    <Layout>
      <Canonical />
      <style jsx global>{`
        h2[id]::before {
          display: block;
          content: ' ';
          margin-top: -8rem;
          height: 8rem;
          visibility: hidden;
          pointer-events: none;
        }
      `}</style>
      <Yoast seo={postData.seo} />
      <Section>
        <div className="grid gap-4 lg:grid lg:auto-rows-min lg:grid-cols-12 lg:gap-4">
          <div className="order-1 text-center lg:col-span-8">
            <Heading2 as="h1" className="text-grey-10">
              {postData.title}
            </Heading2>
            {postData.featuredImage && (
              <div className="mt-8 overflow-hidden rounded-lg leading-0">
                <Image
                  src={postData.featuredImage.node.sourceUrl}
                  objectFit="cover"
                  alt={postData.title}
                  width={943}
                  height={943 / 2}
                  priority
                />
              </div>
            )}
          </div>

          <div className="order-2 lg:order-first lg:col-span-2 lg:row-span-2">
            <div className="sticky top-32 flex flex-col">
              <div className="order-2 mt-4 flex lg:order-1">
                <div className="flex-shrink-0">
                  <span className="sr-only">{postData.author.node.name}</span>
                  <div className="relative h-10 w-10">
                    <Image
                      className="rounded-full"
                      src={postData.author.node.avatar.url}
                      layout="fill"
                      alt={postData.author.node.name}
                    />
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-grey-10">
                    {postData.author.node.name}
                  </p>
                  <div className="flex space-x-1 text-sm text-grey-08 lg:flex-col lg:space-x-0">
                    <DateFormatter dateString={postData.date} />
                    <span className="lg:hidden" aria-hidden="true">
                      ·
                    </span>
                    <ReadTime content={postData.content} />
                    <span className="lg:hidden" aria-hidden="true">
                      ·
                    </span>
                    <ReadCount slug={postData.slug} />
                  </div>
                </div>
              </div>
              {postData.categories && (
                <div className="order-1 mt-6 text-sm font-medium text-blue-08 lg:order-2">
                  {postData.categories.edges.map(({ node }, i, arr) => (
                    <span key={node.id}>
                      <Link href={`/category/${node.slug}`} passHref>
                        <a className="hover:underline lg:block">{node.name}</a>
                      </Link>
                      <span className="lg:hidden">
                        {arr.length - 1 === i ? '\u00A0' : <>, </>}
                      </span>
                    </span>
                  ))}
                </div>
              )}
              {contents.length >= 1 && (
                <div className="order-last mt-4">
                  <p className="text-sm font-medium text-grey-10">Contents</p>
                  <ol className="mt-2 space-y-2 text-sm text-grey-08">
                    {contents}
                  </ol>
                </div>
              )}
            </div>
          </div>

          <div className="order-3 lg:col-span-8">
            <div className="prose prose-lg mx-auto mt-6 prose-headings:font-semibold prose-headings:text-grey-10 prose-h4:text-xl">
              {content}
            </div>
          </div>

          <div className="order-4 lg:order-2 lg:col-span-2 lg:row-span-2">
            <div className="sticky top-32">
              <div className="mt-4 rounded-lg bg-blue-02 p-4 text-blue-10 shadow-md">
                {!subscribed ? (
                  <>
                    <p className="font-medium">
                      Get sales tips and strategies delivered straight to your
                      inbox.
                    </p>
                    <form
                      onSubmit={blogSubscribe}
                      className="mt-4 flex flex-col gap-y-4"
                    >
                      <label htmlFor="emailAddress" className="w-full">
                        <span className="sr-only">Email</span>
                        <InputMedium
                          type="email"
                          autoComplete="email"
                          name="email"
                          id="email"
                          placeholder="Enter your email"
                          required
                        />
                      </label>
                      <ButtonSmall type="submit" className="w-full">
                        Subscribe Now
                      </ButtonSmall>
                    </form>
                  </>
                ) : (
                  <p className="text-center text-lg">Thanks for subscribing…</p>
                )}
              </div>
              <div className="mt-4 rounded-lg bg-blue-02 p-4 text-blue-10 shadow-md">
                <p className="text-center font-medium lg:text-left">
                  Yesware will help you generate more sales right from your
                  inbox. Try our{' '}
                  <a
                    href="/platform/outlook"
                    className="text-blue-08 underline"
                  >
                    Outlook add-on
                  </a>{' '}
                  or{' '}
                  <a href="/platform/gmail" className="text-blue-08 underline">
                    Gmail Chrome extension
                  </a>{' '}
                  for free, forever!
                </p>
              </div>
            </div>
          </div>
        </div>

        <PostCta />
        <RelatedPosts posts={relatedPosts} />
        <BlogCta />
      </Section>
    </Layout>
  );
};

Post.propTypes = {
  postData: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    featuredImage: PropTypes.shape({
      node: PropTypes.shape({
        sourceUrl: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    categories: PropTypes.shape([]),
    title: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    author: PropTypes.shape({
      node: PropTypes.shape({
        name: PropTypes.string.isRequired,
        avatar: PropTypes.shape({
          url: PropTypes.string.isRequired,
        }).isRequired,
      }).isRequired,
    }).isRequired,
    date: PropTypes.string.isRequired,
    seo: PropTypes.shape({}).isRequired,
  }).isRequired,
  relatedPosts: PropTypes.array.isRequired,
};

export const getStaticPaths = async () => ({
  paths: [],
  fallback: 'blocking',
});

export const getStaticProps = async ({ params }) => {
  const data = await getPost(params.slug);

  if (!data.post) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      postData: data.post,
      relatedPosts: data.related,
    },
    revalidate: 900,
  };
};

export default Post;
